import { inject, observer } from "mobx-react";
import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import AboutUs from "./features/public/pages/AboutUs";
import { AdminRoutes, PrivateRoutes, PublicOnlyRoutes, PublicRoutes } from "./shared/routing/authRouter";
import HomeDetails from "./features/home/pages/HomeDetails";
import Footer from "./shared/layout/Footer";
import ManageWebsite from "./features/manageWebsite/pages/ManageWebsite";
import NotFoundPage from "./features/public/pages/NotFoundPage";
import Billing from "./features/billing/pages/Billing";
import CaregiverDetailsPage from "./features/caregiver/pages/CaregiverDetailsPage";
import CaregiverDailyBehaviorLogPage from "./features/caregiver/pages/CaregiverDailyBehaviorLogPage";
import CaregiverDailyNotesPage from "./features/caregiver/pages/CaregiverDailyNotesPage";
import CaregiverResidentAdlPage from "./features/caregiver/pages/CaregiverResidentAdlPage";
import CaregiverResidentEmarPage from "./features/caregiver/pages/CaregiverResidentEmarPage";
import CaregiverIncidentReportPage from "./features/caregiver/pages/CaregiverIncidentReportPage";
import DashboardFindCaregiver from "./features/dashboard/pages/DashboardFindCaregiver";
import DashboardFindJob from "./features/dashboard/pages/DashboardFindJob";
import DashboardListings from "./features/dashboard/pages/DashboardListings";
import DashboardNurse from "./features/dashboard/pages/DashboardNurse";
import DashboardProviderNurse from "./features/dashboard/pages/DashboardProviderNurse";
import DashboardProvider from "./features/dashboard/pages/DashboardProvider";
import DashboardEmployee from "./features/dashboard/pages/DashboardEmployee";
import CaregiverAttendancePage from "./features/dashboard/pages/CaregiverAttendancePage";
import DashboardFamilyMember from "./features/dashboard/pages/DashboardFamilyMember";
import FacilityAdminDashboard from "./features/dashboard/pages/FacilityAdminDashboard";
import FacilityWorkerDashboard from "./features/dashboard/pages/FacilityWorkerDashboard";
import DashboardMhp from "./features/dashboard/pages/DashboardMhp";
import DashboardVendor from "./features/dashboard/pages/DashboardVendor";
import SharedContentPage from "./features/dashboard/pages/SharedContentPage";
import ProviderDirectoryMapPage from "./features/providerDirectory/pages/ProviderDirectoryMapPage";
import BinderPage from "./features/document/pages/BinderPage";
import FaxAuditHistoryPage from "./features/document/pages/FaxAuditHistoryPage";
import DocumentPage from "./features/document/pages/DocumentPage";
import HomeStaffManagementPage from "./features/home/pages/HomeStaffManagementPage";
import ChangePassword from "./features/auth/pages/ChangePassword";
import ConfirmRegistration from "./features/auth/pages/ConfirmRegistration";
import ForgotPassword from "./features/auth/pages/ForgotPassword";
import Register from "./features/auth/pages/Register";
import SignIn from "./features/auth/pages/SignIn";
import Profile from "./features/profile/pages/Profile";
import Workspaces from "./features/workspaces/pages/Workspaces";
import PublishWeb from "./features/publishWeb/pages/PublishWeb";
import UserReminder from "./features/reminder/pages/UserReminder";
import ResidentDetailsPage from "./features/resident/pages/ResidentDetailsPage";
import ProviderDailyBehaviorLogPage from "./features/resident/pages/ProviderDailyBehaviorLogPage";
import CareActivityPage from "./features/home/pages/CareActivityPage";
import SharedCareActivityPage from "./features/shared/pages/SharedCareActivityPage";
import SharedFacilityTasksPage from "./features/shared/pages/SharedFacilityTasksPage";
import ResidentSchedulesPage from "./features/resident/pages/ResidentSchedulesPage";
import { SHOW_RESIDENT_SCHEDULES } from "./constants/uiFlags";
import ProviderDailyNotesPage from "./features/resident/pages/ProviderDailyNotesPage";
import ProviderResidentEntryTrackerPage from "./features/resident/pages/ProviderResidentEntryTrackerPage";
import ProviderResidentAdlPage from "./features/resident/pages/ProviderResidentAdlPage";
import ProviderIncidentReportPage from "./features/resident/pages/ProviderIncidentReportPage";
import ProviderResidentCarePlanPage from "./features/resident/pages/ProviderResidentCarePlanPage";
import ProviderResidentEmarPage from "./features/resident/pages/ProviderResidentEmarPage";
import NurseResidentProfilePage from "./features/resident/pages/NurseResidentProfilePage";
import NurseResidentDelegationPage from "./features/resident/pages/NurseResidentDelegationPage";
import NurseDelegationReferralFormPage from "./features/resident/pages/NurseDelegationReferralFormPage";
import ResidentNcpPage from "./features/resident/pages/ResidentNcpPage";
import EsfCarePlanPage from "./features/resident/pages/EsfCarePlanPage";
import SharedResidentAdlPage from "./features/shared/pages/SharedResidentAdlPage";
import SharedResidentCarePlanPage from "./features/shared/pages/SharedResidentCarePlanPage";
import SharedResidentDailyBehaviorLogPage from "./features/shared/pages/SharedResidentDailyBehaviorLogPage";
import SharedResidentDailyNotesPage from "./features/shared/pages/SharedResidentDailyNotesPage";
import SharedResidentEntryTrackerPage from "./features/shared/pages/SharedResidentEntryTrackerPage";
import SharedResidentEmarPage from "./features/shared/pages/SharedResidentEmarPage";
import SharedResidentIncidentReportPage from "./features/shared/pages/SharedResidentIncidentReportPage";
import ReviewPage from "./features/review/pages/ReviewPage";
import Root from "./features/public/pages/Root";
import RelationshipCenterPage from "./features/relationship/pages/RelationshipCenterPage";
import SearchWithAIModal from "./components/general/searchWithAI";
import Support from "./features/support/pages/Support";
import PrivacyPolicy from "./features/public/pages/PrivacyPolicy";
import Pricing from "./features/public/pages/Pricing";
import BookDemoPage from "./features/public/pages/BookDemoPage";
import SharedCalendarPage from "./features/calendar/pages/SharedCalendarPage";
import WhyAfhHub from "./features/public/pages/WhyAfhHub";
import EmarPage from "./features/public/pages/EmarPage";
import AdlPage from "./features/public/pages/AdlPage";
import TermsOfService from "./features/public/pages/TermsOfService";
import HipaaCompliance from "./features/public/pages/HipaaCompliance";
import PublicIntakePage from "./features/intake/pages/PublicIntakePage";
import IntakeReceivedPage from "./features/intake/pages/IntakeReceivedPage";
import { RouterAddress } from "./constants/RouterAddress";
import { AuthContextProvider } from "./contexts/authContext";
import BusinessAssociateAgreement from "./features/public/pages/BusinessAssociateAgreement";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ScrollTop } from 'primereact/scrolltop';
import PlatformFeatures from "./features/public/pages/PlatformFeatures";
import ReleaseNotes from "./features/public/pages/ReleaseNotes";
import Tutorials from "./features/public/pages/Tutorials";
import LiveDemo from "./features/public/pages/LiveDemo";
import EmailPreferences from "./features/public/pages/EmailPreferences";
import AdminPage from "./features/admin/pages/AdminPage";
import { TourProvider } from "./shared/tour/TourProvider";
import { buildJsErrorTelemetry, buildPageViewTelemetry, extractClickTelemetry } from "./utils/frontendTelemetry";
import NurseResidentAssessmentPage from "./features/resident/pages/NurseResidentAssessmentPage";
import NurseResidentNcpPage from "./features/resident/pages/NurseResidentNcpPage";
import ProviderResidentAssessmentPage from "./features/resident/pages/ProviderResidentAssessmentPage";
import NurseResidentPodiatristPage from "./features/resident/pages/NurseResidentPodiatristPage";

const FrontendTelemetryBridge = ({ backendAPIStore }: { backendAPIStore: any }) => {
  const location = useLocation();

  React.useEffect(() => {
    backendAPIStore.queueFrontendTelemetry?.([buildPageViewTelemetry(location.pathname)]);
  }, [backendAPIStore, location.pathname]);

  React.useEffect(() => {
    let lastClickSignature = "";
    let lastClickAt = 0;

    const handleError = (event: ErrorEvent) => {
      backendAPIStore.queueFrontendTelemetry?.([buildJsErrorTelemetry({
        errorType: "error",
        message: event.message,
        stack: event.error?.stack,
        source: event.filename,
      })]);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      backendAPIStore.queueFrontendTelemetry?.([buildJsErrorTelemetry({
        errorType: "unhandledrejection",
        message: String(event.reason?.message || event.reason || "Unhandled promise rejection"),
        stack: event.reason?.stack,
      })]);
    };

    const handleDocumentClick = (event: MouseEvent) => {
      const telemetryEvent = extractClickTelemetry(event.target);
      if (!telemetryEvent) {
        return;
      }

      const signature = `${telemetryEvent.page}|${telemetryEvent.action}|${telemetryEvent.label}`;
      const now = Date.now();
      if (signature === lastClickSignature && (now - lastClickAt) < 500) {
        return;
      }

      lastClickSignature = signature;
      lastClickAt = now;
      backendAPIStore.queueFrontendTelemetry?.([telemetryEvent]);
    };

    const flushIfBackgrounded = () => {
      if (document.visibilityState === "hidden") {
        void backendAPIStore.flushFrontendTelemetry?.();
      }
    };

    const flushOnPageHide = () => {
      void backendAPIStore.flushFrontendTelemetry?.();
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("pagehide", flushOnPageHide);
    document.addEventListener("visibilitychange", flushIfBackgrounded);
    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("pagehide", flushOnPageHide);
      document.removeEventListener("visibilitychange", flushIfBackgrounded);
      document.removeEventListener("click", handleDocumentClick, true);
      void backendAPIStore.flushFrontendTelemetry?.();
    };
  }, [backendAPIStore]);

  return null;
};

class FrontendErrorBoundary extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.props.backendAPIStore?.queueFrontendTelemetry?.([buildJsErrorTelemetry({
      errorType: "react_error_boundary",
      message: error.message,
      stack: error.stack,
    })]);
  }

  render() {
    return this.props.children;
  }
}

const LegacyFacilityTasksRedirect = () => {
  const { homeId = "" } = useParams();
  return <Navigate to={RouterAddress.getHomeFacilityTasksPagePath(homeId)} replace />;
};

@inject("authStore", "backendAPIStore")
@observer
export class App extends React.Component<any, any> {
  render() {
    const { authStore, backendAPIStore } = this.props;
    return (
      <AuthContextProvider {...authStore}>
        <BrowserRouter>
          <TourProvider>
            <FrontendErrorBoundary backendAPIStore={backendAPIStore}>
              <FrontendTelemetryBridge backendAPIStore={backendAPIStore} />
              <div className="app-shell">
                <main className="app-content">
                  <Routes>
                    <Route element={<PublicRoutes />}>
                      <Route path={RouterAddress.APP_ROOT} element={<Root />} />
                      <Route path={RouterAddress.PUBLIC_WEBSITE} element={<PublishWeb />} />
                      <Route path={RouterAddress.PUBLIC_WEBSITE_SUBPAGE} element={<PublishWeb />} />
                      <Route path={RouterAddress.PUBLISH_WEB} element={<PublishWeb />} />
                      <Route path={RouterAddress.PUBLISH_WEB_SUBPAGE} element={<PublishWeb />} />
                      <Route path={RouterAddress.LISTINGS} element={<DashboardListings />} />
                      <Route path={RouterAddress.PRIVACY_PAGE} element={<PrivacyPolicy />} />
                      <Route path={RouterAddress.TERMS_OF_SERVICE_PAGE} element={<TermsOfService />} />
                      <Route path={RouterAddress.BUSINESS_ASSOCIATE_AGREEMENT_PAGE} element={<BusinessAssociateAgreement />} />
                      <Route path={RouterAddress.HIPAA_COMPLIANCE_PAGE} element={<HipaaCompliance />} />
                      <Route path={RouterAddress.PUBLIC_INTAKE} element={<PublicIntakePage />} />
                      <Route path={RouterAddress.LEGACY_PUBLIC_INTAKE} element={<PublicIntakePage />} />
                      <Route path={RouterAddress.ABOUT_US_PAGE} element={<AboutUs />} />
                      <Route path={RouterAddress.WHY_USE_HUB_PAGE} element={<WhyAfhHub />} />
                      <Route path={RouterAddress.LEGACY_WHY_AFH_HUB_PAGE} element={<Navigate to={RouterAddress.WHY_USE_HUB_PAGE} replace />} />
                      <Route path={RouterAddress.EMAR_PAGE} element={<EmarPage />} />
                      <Route path={RouterAddress.ADL_PAGE} element={<AdlPage />} />
                      <Route path={RouterAddress.PRICING_PAGE} element={<Pricing />} />
                      <Route path={RouterAddress.BOOK_DEMO_PAGE} element={<BookDemoPage />} />
                      <Route path={RouterAddress.LIVE_DEMO_PAGE} element={<LiveDemo />} />
                      <Route path={RouterAddress.LEGACY_LIVE_DEMO_PAGE} element={<Navigate to={RouterAddress.LIVE_DEMO_PAGE} replace />} />
                      <Route path={RouterAddress.PLATFORM_FEATURES_PAGE} element={<PlatformFeatures />} />
                      <Route path={RouterAddress.TUTORIALS_PAGE} element={<Tutorials />} />
                      <Route path={RouterAddress.RELEASE_NOTES_PAGE} element={<ReleaseNotes />} />
                      <Route path={RouterAddress.EMAIL_PREFERENCES_PAGE} element={<EmailPreferences />} />
                      <Route path="*" element={<NotFoundPage />} />

                      <Route element={<PublicOnlyRoutes authStore={authStore} />}>
                        <Route path={RouterAddress.LOGIN_PAGE} element={<SignIn />} />
                        <Route path={RouterAddress.SIGN_UP} element={<Register />} />
                        <Route path={RouterAddress.CONFIRM_REGISTRATION} element={<ConfirmRegistration />} />
                        <Route path={RouterAddress.FORGOT_PASSWORD} element={<ForgotPassword />} />
                        <Route path={RouterAddress.RESET_PASSWORD} element={<ChangePassword />} />
                      </Route>
                    </Route>

                    <Route element={<PrivateRoutes authStore={authStore} />}>
                      <Route path={RouterAddress.PROFILE} element={<Profile />} />
                      <Route path={RouterAddress.FAX_AUDIT_HISTORY} element={<FaxAuditHistoryPage />} />
                      <Route path={RouterAddress.WORKSPACES} element={<Workspaces />} />
                      <Route path={RouterAddress.FACILITY_ADMIN_DASHBOARD} element={<FacilityAdminDashboard />} />
                      <Route path={RouterAddress.FACILITY_WORKER_HOME} element={<FacilityWorkerDashboard />} />
                      <Route path={RouterAddress.SUPPORT} element={<Support />} />
                      <Route path={RouterAddress.SHARED_CALENDAR} element={<SharedCalendarPage />} />
                      <Route path={RouterAddress.SHARED_CONTENT_PAGE} element={<SharedContentPage />} />
                      <Route path={RouterAddress.BILLING} element={<Billing />} />
                      <Route path={RouterAddress.CONNECTIONS_PAGE} element={<RelationshipCenterPage />} />
                      <Route element={<AdminRoutes authStore={authStore} />}>
                        <Route path={RouterAddress.ADMIN_PAGE} element={<AdminPage />} />
                        <Route path={RouterAddress.PROVIDER_DIRECTORY_MAP} element={<ProviderDirectoryMapPage />} />
                      </Route>

                      <Route path={RouterAddress.ADMIN_HOME} element={<Navigate to={RouterAddress.PROVIDER_HOME} replace />} />
                      <Route path={RouterAddress.PROVIDER_HOME} element={<DashboardProvider />} />
                      <Route path={RouterAddress.HOME_INTAKE_RECEIVED_PAGE} element={<IntakeReceivedPage />} />
                      <Route path={RouterAddress.PROVIDER_NURSE_HOME} element={<DashboardProviderNurse />} />
                      <Route path={RouterAddress.AFH_DETAILS_PAGE} element={<HomeDetails />} />

                      <Route path={RouterAddress.EMPLOYEE_HOME} element={<DashboardEmployee />} />
                      <Route path={RouterAddress.CAREGIVER_HOME} element={<DashboardEmployee />} />
                      <Route path={RouterAddress.CAREGIVER_ATTENDANCE_PAGE} element={<CaregiverAttendancePage />} />
                      <Route path={RouterAddress.FACILITY_WORKER_ATTENDANCE_PAGE} element={<CaregiverAttendancePage />} />
                      <Route path={RouterAddress.FAMILY_MEMBER_HOME} element={<DashboardFamilyMember />} />
                      <Route path={RouterAddress.VENDOR_HOME} element={<DashboardVendor />} />
                      <Route path={RouterAddress.NURSE_HOME} element={<DashboardNurse />} />
                      <Route path={RouterAddress.MHP_HOME} element={<DashboardMhp />} />

                      <Route path={RouterAddress.AI_COMPANION} element={<SearchWithAIModal />} />
                      <Route path={RouterAddress.MANAGE_WEBSITE} element={<ManageWebsite />} />

                      <Route path={RouterAddress.HOME_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.CAREGIVER_JOB_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.FACILITY_WORKER_JOB_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.NURSE_JOB_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.MHP_JOB_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.SHARED_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.HOME_STAFF_MANAGEMENT_PAGE} element={<HomeStaffManagementPage />} />
                      <Route path={RouterAddress.HOME_CARE_ACTIVITY_PAGE} element={<CareActivityPage />} />
                      <Route path={RouterAddress.SHARED_HOME_CARE_ACTIVITY_PAGE} element={<SharedCareActivityPage />} />
                      <Route path={RouterAddress.HOME_FACILITY_TASKS_PAGE} element={<SharedFacilityTasksPage />} />
                      <Route path={RouterAddress.CAREGIVER_HOME_FACILITY_TASKS_PAGE} element={<LegacyFacilityTasksRedirect />} />
                      <Route path={RouterAddress.FACILITY_WORKER_HOME_FACILITY_TASKS_PAGE} element={<LegacyFacilityTasksRedirect />} />
                      <Route path={RouterAddress.SHARED_HOME_FACILITY_TASKS_PAGE} element={<SharedFacilityTasksPage />} />
                      {SHOW_RESIDENT_SCHEDULES ? <Route path={RouterAddress.RESIDENT_SCHEDULES_PAGE} element={<ResidentSchedulesPage />} /> : null}
                      <Route path={RouterAddress.HOME_DOCUMENTS_PAGE} element={<DocumentPage />} />
                      <Route path={RouterAddress.RESIDENT_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.PROVIDER_NURSE_RESIDENT_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.NURSE_RESIDENT_BINDER_PAGE} element={<BinderPage />} />
                      <Route path={RouterAddress.RESIDENT_DOCUMENTS_PAGE} element={<DocumentPage />} />
                      <Route path={RouterAddress.CAREGIVER_DOCUMENTS_PAGE} element={<DocumentPage />} />
                      <Route path={RouterAddress.USER_DOCUMENTS_PAGE} element={<DocumentPage />} />

                      <Route path={RouterAddress.USER_REVIEWS_PAGE} element={<ReviewPage />} />

                      <Route path={RouterAddress.FIND_CAREGIVERS} element={<DashboardFindCaregiver />} />
                      <Route path={RouterAddress.SEARCH_JOB} element={<DashboardFindJob />} />
                      <Route path={RouterAddress.RESIDENT_DETAILS_PAGE} element={<ResidentDetailsPage />} />
                      <Route path={RouterAddress.RESIDENT_NCP_PAGE} element={<ResidentNcpPage />} />
                      <Route path={RouterAddress.RESIDENT_ESF_CARE_PLAN_PAGE} element={<EsfCarePlanPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_PROFILE_PAGE} element={<NurseResidentProfilePage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_ASSESSMENT_PAGE} element={<ProviderResidentAssessmentPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_ASSESSMENT_EDITOR_PAGE} element={<NurseResidentAssessmentPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_DELEGATION_PAGE} element={<NurseResidentDelegationPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_DELEGATION_FORM_PAGE} element={<NurseDelegationReferralFormPage />} />
                      <Route path={RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE} element={<NurseResidentProfilePage />} />
                      <Route path={RouterAddress.PROVIDER_NURSE_RESIDENT_NCP_PAGE} element={<NurseResidentNcpPage />} />
                      <Route path={RouterAddress.PROVIDER_NURSE_RESIDENT_ASSESSMENT_PAGE} element={<NurseResidentAssessmentPage />} />
                      <Route path={RouterAddress.PROVIDER_NURSE_RESIDENT_DELEGATION_PAGE} element={<NurseResidentDelegationPage />} />
                      <Route path={RouterAddress.PROVIDER_NURSE_RESIDENT_PODIATRIST_PAGE} element={<NurseResidentPodiatristPage />} />
                      <Route path={RouterAddress.PROVIDER_NURSE_DELEGATION_FORM_PAGE} element={<NurseDelegationReferralFormPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_PROFILE_PAGE} element={<NurseResidentProfilePage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_ASSESSMENT_PAGE} element={<ProviderResidentAssessmentPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_NCP_PAGE} element={<ResidentNcpPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_ESF_CARE_PLAN_PAGE} element={<EsfCarePlanPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_CARE_PLAN_PAGE} element={<SharedResidentCarePlanPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_EMAR_PAGE} element={<SharedResidentEmarPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_ADL_PAGE} element={<SharedResidentAdlPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE} element={<SharedResidentDailyBehaviorLogPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_INCIDENT_REPORT_PAGE} element={<SharedResidentIncidentReportPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_DAILY_NOTES_PAGE} element={<SharedResidentDailyNotesPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_ENTRY_TRACKER_PAGE} element={<SharedResidentEntryTrackerPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_DELEGATION_PAGE} element={<NurseResidentDelegationPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_DELEGATION_FORM_PAGE} element={<NurseDelegationReferralFormPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_PODIATRIST_PAGE} element={<NurseResidentPodiatristPage />} />
                      <Route path={RouterAddress.SHARED_RESIDENT_BINDER_PAGE} element={<BinderPage />} />

                      <Route path={RouterAddress.NURSE_RESIDENT_ASSESSMENT_PAGE} element={<NurseResidentAssessmentPage />} />
                      <Route path={RouterAddress.NURSE_RESIDENT_PROFILE_PAGE} element={<NurseResidentProfilePage />} />
                      <Route path={RouterAddress.NURSE_RESIDENT_NCP_PAGE} element={<NurseResidentNcpPage />} />
                      <Route path={RouterAddress.NURSE_RESIDENT_PODIATRIST_PAGE} element={<NurseResidentPodiatristPage />} />
                      {/* <Route path={RouterAddress.NURSE_ASSESSMENT_FORM_PAGE} element={<NurseAssessmentFormPage />} /> */}

                      <Route path={RouterAddress.NURSE_RESIDENT_DELEGATION_PAGE} element={<NurseResidentDelegationPage />} />
                      {/* <Route path={RouterAddress.NURSE_DELEGATION_REFERRAL_FORM_PAGE} element={<NurseDelegationReferralFormPage />} /> */}
                      <Route path={RouterAddress.NURSE_DELEGATION_FORM_PAGE} element={<NurseDelegationReferralFormPage />} />

                      <Route path={RouterAddress.CAREGIVER_DETAILS_PAGE} element={<CaregiverDetailsPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_CARE_PLAN_PAGE} element={<ProviderResidentCarePlanPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_EMAR_PAGE} element={<ProviderResidentEmarPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_ADL_PAGE} element={<ProviderResidentAdlPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE} element={<ProviderDailyBehaviorLogPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_INCIDENT_REPORT_PAGE} element={<ProviderIncidentReportPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_DAILY_NOTES_PAGE} element={<ProviderDailyNotesPage />} />
                      <Route path={RouterAddress.PROVIDER_RESIDENT_ENTRY_TRACKER_PAGE} element={<ProviderResidentEntryTrackerPage />} />
                      <Route path={RouterAddress.CAREGIVER_RESIDENT_CARE_PLAN_PAGE} element={<CaregiverDetailsPage />} />
                      <Route path={RouterAddress.CAREGIVER_RESIDENT_EMAR_PAGE} element={<CaregiverResidentEmarPage />} />
                      <Route path={RouterAddress.CAREGIVER_RESIDENT_ADL_PAGE} element={<CaregiverResidentAdlPage />} />
                      <Route path={RouterAddress.CAREGIVER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE} element={<CaregiverDailyBehaviorLogPage />} />
                      <Route path={RouterAddress.CAREGIVER_RESIDENT_INCIDENT_REPORT_PAGE} element={<CaregiverIncidentReportPage />} />
                      <Route path={RouterAddress.CAREGIVER_RESIDENT_DAILY_NOTES_PAGE} element={<CaregiverDailyNotesPage />} />

                      <Route path={RouterAddress.USER_REMINDER_PAGE} element={<UserReminder />} />
                    </Route>
                  </Routes>
                </main>
                <Footer />
              </div>
            </FrontendErrorBoundary>
          </TourProvider>
        </BrowserRouter>
        <ConfirmDialog />
        <ScrollTop />
      </AuthContextProvider>
    );
  }
}
